// postMessage contract between the lesson page (parent) and the stage iframe.
// The parent owns playback (narration audio, play/pause, which step); the
// iframe owns the DOM actions. Both sides are same-origin, so the origin is
// checked against window.location.origin on receipt.

export const STAGE_QUERY_PARAM = "academy";
export const STAGE_HARNESS_PARAM = "harness";
/** window.name prefix the stage sets on itself so a reload keeps stage mode. */
export const STAGE_WINDOW_NAME_PREFIX = "academy:";

export type ParentToStageMessage =
  /** Start (or resume) running from this step. */
  | { type: "academy:play"; stepIndex: number }
  /** Freeze timers; the cursor stays where it is. */
  | { type: "academy:pause" }
  /**
   * Jump to a step. The stage reloads itself to the scene's startRoute and
   * replays earlier steps instantly (no cursor glide, no waits) before
   * running the requested step normally.
   */
  | { type: "academy:seek"; stepIndex: number; autoplay: boolean }
  /** Reset to the beginning (reload). */
  | { type: "academy:restart" };

export type StageToParentMessage =
  /** The app booted on fixtures, the scene loaded, and the first target exists. */
  | { type: "academy:ready"; sceneId: string; stepCount: number }
  /** A step's actions started. */
  | { type: "academy:step-start"; stepIndex: number }
  /** A step's actions finished (the parent still waits for narration to end). */
  | { type: "academy:step-done"; stepIndex: number }
  /** Every step has run. */
  | { type: "academy:done" }
  /** An action failed (usually a missing selector); the parent shows a notice. */
  | { type: "academy:error"; stepIndex: number; message: string };

export const isStageToParent = (data: unknown): data is StageToParentMessage =>
  !!data && typeof data === "object" && typeof (data as any).type === "string" && (data as any).type.startsWith("academy:");

export const isParentToStage = (data: unknown): data is ParentToStageMessage => isStageToParent(data);

/** Build the iframe URL for a scene. */
export const stageUrl = (sceneId: string, startRoute: string, harness = false): string => {
  const params = new URLSearchParams({ [STAGE_QUERY_PARAM]: sceneId });
  if (harness) params.set(STAGE_HARNESS_PARAM, "1");
  return `${startRoute}?${params.toString()}`;
};
